// The consent banner for app.itsbaba.com. The gate (window.babaConsent) is the shared one from
// @baba/attribution/consent-boot, injected into index.html by vite.config.ts; www.itsbaba.com has
// the same component in Tailwind, and apps/news has its own older gate (components/consent).
import { useEffect, useState, useSyncExternalStore } from 'react'
import { CONSENT_OPEN_EVENT, type ConsentGate } from '@baba/attribution/consent'

const COPY = {
  title: 'Cookies, analytics and ads',
  body: 'With your permission we use cookies to count visits (Google Analytics, PostHog) and to measure our ads (Google, Meta, TikTok). Nothing optional loads unless you accept, and baba works the same either way.',
  sale: 'In some places you can ask us not to sell or share your personal information for advertising. This turns off ad matching and limits what Meta and TikTok may do with the data.',
  gpc: 'Your browser sends a Global Privacy Control signal, which we treat as that request.',
  accept: 'Accept',
  reject: 'Reject',
  optOut: 'Do not sell or share',
  optIn: 'Allow sharing again',
  policy: 'Privacy Policy',
  close: 'Close',
} as const

const gate = (): ConsentGate | null => (window as unknown as { babaConsent?: ConsentGate }).babaConsent ?? null
const subscribe = (onChange: () => void) => gate()?.subscribe(onChange) ?? (() => {})
// One string per distinct state, so the snapshot is stable between changes.
const snapshot = () => {
  const g = gate()
  const s = g?.get()
  return g && s ? [g.requiresPrompt() ? 'required' : 'open', g.needsChoice() ? 'owed' : 'made', s.do_not_sell ? 'optout' : 'in', g.gpc ? 'gpc' : 'nogpc'].join('|') : 'pending'
}

export function openConsentChoices() {
  gate()?.open()
}

export default function ConsentBanner() {
  const view = useSyncExternalStore(subscribe, snapshot, () => 'pending')
  const [manual, setManual] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  useEffect(() => {
    const open = () => { setDismissed(false); setManual(true) }
    window.addEventListener(CONSENT_OPEN_EVENT, open)
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, open)
  }, [])
  if (view === 'pending') return null
  const [mode, owed, sale, gpc] = view.split('|')
  if (!(manual || (owed === 'owed' && !dismissed))) return null
  const g = gate()
  const required = mode === 'required'
  const close = () => { setDismissed(true); setManual(false) }
  const button = {
    minHeight: 40, minWidth: 112, padding: '0 16px', borderRadius: 999, fontSize: 13.5, fontWeight: 600,
    border: '1px solid var(--ink, currentColor)', background: 'transparent', color: 'var(--ink, inherit)', cursor: 'pointer',
  } as const
  return (
    <section
      role="region"
      aria-label="Privacy choices"
      style={{
        position: 'fixed', insetInline: 16, bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', zIndex: 1000,
        maxWidth: 720, marginInline: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '14px 20px',
        padding: '18px 20px', background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border-strong)',
        borderRadius: 16, boxShadow: 'var(--shadow-lg)',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>{COPY.title}</h2>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--muted)' }}>
          {required ? COPY.body : COPY.sale}{' '}
          <a href="https://www.itsbaba.com/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>{COPY.policy}</a>
        </p>
        {!required && gpc === 'gpc' ? <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--muted)' }}>{COPY.gpc}</p> : null}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {required ? (
          <>
            <button type="button" style={button} onClick={() => { g?.reject(); close() }}>{COPY.reject}</button>
            <button type="button" style={button} onClick={() => { g?.accept(); close() }}>{COPY.accept}</button>
          </>
        ) : (
          <button type="button" style={button} onClick={() => { g?.optOutOfSale(sale !== 'optout'); close() }}>{sale === 'optout' ? COPY.optIn : COPY.optOut}</button>
        )}
        {manual ? <button type="button" style={{ ...button, border: 0, minWidth: 0, textDecoration: 'underline', color: 'var(--muted)' }} onClick={close}>{COPY.close}</button> : null}
      </div>
    </section>
  )
}
